<script>
import cloneDeep from 'lodash/cloneDeep'
import tourMixin from '@src/mixins/tour'
import { authComputed } from '@state/helpers'
import lazyLoadComponent from '@router/lazyload-component'
import { cities as TaiwanCity } from '@utils/taiwan-city-area'

const initData = () => {
  return {
    state: 2,
    hidden: 0,
    name: '',
    alias: '',
    type: '',
    description: '',
    introtext: '',
    notice: '',
    introimage: '',
    image: '',
    video: '',
    orderExtrafields: [],
    allowOverlap: 0,
    staffs: [],
  }
}
export default {
  components: {
    BaseInputFloatingLabel: () =>
      lazyLoadComponent(import('@components/BaseInputFloatingLabel')),
    BaseUpload: () => lazyLoadComponent(import('@components/BaseUpload')),
  },
  mixins: [tourMixin],
  tourName: 'productBasic',
  props: {
    itemType: {
      type: String,
      required: true,
    },
    defaultValue: {
      type: Object,
      default: () => initData(),
    },
  },
  data() {
    return {
      TaiwanCityList: Object.freeze(TaiwanCity),
      submitData: cloneDeep(this.defaultValue),
      submitRules: {
        name: [
          {
            required: true,
            message: '請填寫名稱',
            trigger: 'blur',
          },
        ],
        alias: [
          {
            required: true,
            message: '請填寫別名',
            trigger: 'blur',
          },
          {
            pattern: /^[a-zA-Z0-9-]+$/,
            message: '請使用英文大小寫字母、數字或英文半形連字號組合',
            trigger: 'blur',
          },
        ],
        description: [
          {
            required: true,
            message: '請填寫描述',
            trigger: 'blur',
          },
        ],
        video: [
          {
            pattern: /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/,
            message: '請輸入有效的 YouTube 影片網址',
            trigger: 'blur',
          },
        ],
      },
    }
  },
  computed: {
    ...authComputed,
    productBaseBookingUrl() {
      return `${this.merchantUrl}/${this.itemType}/`
    },
    isEditMode() {
      return this.defaultValue.id
    },
  },
  created() {
    !this.isEditMode ||
    (this.isEditMode && !this.submitData.orderExtrafields.byProduct)
      ? this.requestExtraFieldbyProduct()
      : this.updateExtraFieldbyProduct(
          this.submitData.orderExtrafields.byProduct
        )
  },
  methods: {
    validate() {
      return this.$refs.form.validate()
    },
    async requestExtraFieldbyProduct() {
      const extraFieldData = await this.$store.dispatch(
        'product/fetchExtraField',
        {
          typeByProduct: this.itemType,
        }
      )
      const filterData = extraFieldData.map((field) => {
        const { id, title, value, required } = field
        return {
          id,
          title,
          value,
          required,
        }
      })
      this.updateExtraFieldbyProduct([...filterData])
    },
    updateExtraFieldbyProduct(value) {
      this.submitData.orderExtrafields = value
    },
  },
}
</script>

<template>
  <el-form
    ref="form"
    :model="submitData"
    :rules="submitRules"
    label-position="top"
    class="el-form--floating"
  >
    <template v-if="isEditMode">
      <h4 class="form-block-title">{{
        $t('PRODUCT.TITLE_STATE_AND_DISPLAY', {
          name: $t(`PRODUCT.${itemType}.NAME`),
        })
      }}</h4>
      <el-row>
        <el-col :span="12">
          <el-form-item
            :label="
              $t('PRODUCT.STATE_LABEL', {
                name: $t(`PRODUCT.${itemType}.NAME`),
              })
            "
            prop="state"
          >
            <el-switch
              v-model="submitData.state"
              active-text="開放"
              inactive-text="關閉"
              :active-value="1"
              :inactive-value="0"
            ></el-switch>
          </el-form-item>
        </el-col>
        <el-col v-if="submitData.state === 1" :span="12">
          <el-form-item label="在商店首頁" prop="hidden">
            <el-switch
              v-model="submitData.hidden"
              active-text="顯示"
              :active-value="0"
              inactive-text="隱藏"
              :inactive-value="1"
            ></el-switch>
          </el-form-item>
        </el-col>
      </el-row>
    </template>

    <h4
      data-tour-text="介紹你的活動吧！讓客戶初步了解活動內容及更多注意事項。"
      class="form-block-title"
      >基本資訊</h4
    >
    <el-form-item
      :label="
        $t('PRODUCT.NAME_LABEL', { name: $t(`PRODUCT.${itemType}.NAME`) })
      "
      prop="name"
    >
      <BaseInputFloatingLabel v-model="submitData.name" />
    </el-form-item>
    <el-form-item
      :label="
        $t('PRODUCT.ALIAS_LABEL', { name: $t(`PRODUCT.${itemType}.NAME`) })
      "
      prop="alias"
    >
      <BaseInputFloatingLabel
        v-model="submitData.alias"
        placeholder="請使用英文字母與數字組合"
      >
        <template v-slot:prepend>{{ productBaseBookingUrl }}</template>
      </BaseInputFloatingLabel>
    </el-form-item>
    <el-form-item
      :label="
        $t('PRODUCT.DESCRIPTION_LABEL', {
          name: $t(`PRODUCT.${itemType}.NAME`),
        })
      "
      prop="description"
    >
      <BaseInputFloatingLabel
        v-model="submitData.description"
        type="textarea"
        :autosize="{ minRows: 2 }"
      />
    </el-form-item>
    <!-- <el-row v-if="!isEditMode">
      <el-col :span="8">
        <el-form-item
          :label="
            $t('PRODUCT.OVERLAP_LABEL', {
              name: $t(`PRODUCT.${itemType}.NAME`),
            })
          "
          prop="allowOverlap"
        >
          <el-switch
            v-model="submitData.allowOverlap"
            active-text="是"
            inactive-text="否"
            :active-value="0"
            :inactive-value="1"
          ></el-switch>
        </el-form-item>
      </el-col>
      <el-col :span="16">
        <el-alert
          title="若您的預訂是一個時段結束後再接續另一場時段者，請開啟此選項以避免施作人員分身乏術。"
          :closable="false"
          type="warning"
          effect="dark"
        ></el-alert>
      </el-col>
    </el-row> -->

    <el-form-item label="注意事項" prop="notice">
      <BaseInputFloatingLabel
        v-model="submitData.notice"
        type="textarea"
        :autosize="{ minRows: 2 }"
      />
    </el-form-item>
    <!-- <template v-if="submitData.orderExtrafields instanceof Array">
      <el-form-item
        v-for="(extrafield, fieldIndex) in submitData.orderExtrafields"
        :key="fieldIndex"
        :label="extrafield.title"
        :prop="`orderExtrafields.${fieldIndex}.value`"
        :rules="{
          required: !!extrafield.required,
          message: `請填寫${extrafield.title}`,
        }"
      >
        <BaseInputFloatingLabel
          v-model="extrafield.value"
          type="textarea"
          :autosize="{ minRows: 2 }"
        />
      </el-form-item>
    </template> -->

    <el-form-item :label="'作業說明'" prop="introtext">
      <BaseInputFloatingLabel
        v-model="submitData.introtext"
        type="textarea"
        :autosize="{ minRows: 2 }"
      />
    </el-form-item>
    
    <h4
      data-tour-text="上傳活動或環境照片吸引客戶的目光吧！"
      class="form-block-title"
      >圖片</h4
    >
    <el-row :gutter="20">
      <el-col :span="12">
        <el-form-item label="目錄縮圖" prop="introimage">
          <BaseUpload
            v-model="submitData.introimage"
            resource="product"
            suggest-size="640/480"
          />
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item label="介紹橫幅形象圖" prop="image">
          <BaseUpload
            v-model="submitData.image"
            resource="product"
            suggest-size="912/300"
          />
        </el-form-item>
      </el-col>
    </el-row>

    <h4
      data-tour-text="有任何精彩的影片也不錯過！立刻輸入影片網址讓客戶看看吧！"
      class="form-block-title"
      >影片</h4
    >
    <el-form-item label="YouTube 影片網址" prop="video">
      <BaseInputFloatingLabel v-model="submitData.video" />
    </el-form-item>
  </el-form>
</template>
